import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Logout as LogoutIcon,
  WarningAmber as WarningIcon,
  NightlightRound as NightlightIcon,
  WbSunny as SunIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import apiService from "../../../../service/Api";
import { tokens } from "../../../../theme";
import { RootState } from "../../../../store/Index";

const API_BASE_URL =
  window.location.hostname === "localhost" ? "http://localhost:8000" : "";

interface CustomUser {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  profile_picture: string | null;
  profile_picture_url: string | null;
  profile_picture_base64: string | null;
  last_login: string | null;
  is_active: boolean;
}

interface UserLoginResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CustomUser[];
}

interface ReportUser {
  user_id: number;
  username: string;
  full_name: string;
  profile_picture: string | null;
  role?: string | null;
}

interface LoginReportResponse {
  report: string;
  date: string;
  timezone: string;
  window: {
    start: string;
    end: string;
  };
  count: number;
  next: string | null;
  previous: string | null;
  users: ReportUser[];
}

interface ForceLogoutResponse {
  message: string;
  users_logged_out_count: number;
  admin_session_preserved: boolean;
}

const getInitials = (name: string) => {
  if (!name) return "U";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const resolveAvatarSource = (
  path: string | null | undefined,
  base64?: string | null
) => {
  if (base64) {
    return base64.startsWith("data:image")
      ? base64
      : `data:image/jpeg;base64,${base64}`;
  }

  if (!path) return undefined;

  if (path.startsWith("http") || path.startsWith("data:")) {
    return path;
  }

  return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

export default function UserLoginMonitor() {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const authUser = useSelector((state: RootState) => state.auth.user);
  const colors = tokens(theme.palette.mode, themeState);
  const navigate = useNavigate();

  const isSuperUser =
    String(authUser?.is_supper_user).toLowerCase() === "true";

  // Login History State
  const [logs, setLogs] = useState<CustomUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState("");
  const [loginDate, setLoginDate] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 8;

  // Midnight Reports State
  const [reportTab, setReportTab] = useState(0);
  const [preReport, setPreReport] = useState<LoginReportResponse | null>(null);
  const [postReport, setPostReport] = useState<LoginReportResponse | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  // Force Logout Dialog State
  const [forceLogoutDialogOpen, setForceLogoutDialogOpen] = useState(false);
  const [forceLogoutLoading, setForceLogoutLoading] = useState(false);

  // Fetch Login History
  const fetchLoginMonitor = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * rowsPerPage;
      let url = `/api/authentication/users/last-login/?page=${page}&page_size=${rowsPerPage}&limit=${rowsPerPage}&offset=${offset}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      if (roleFilter) url += `&role=${encodeURIComponent(roleFilter)}`;
      if (activityFilter) url += `&activity=${encodeURIComponent(activityFilter)}`;
      if (loginDate) url += `&date=${encodeURIComponent(loginDate)}`;

      const response = await apiService.get<UserLoginResponse>(url);
      setLogs(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (e) {
      if (e instanceof AxiosError) {
        const msg =
          e.response?.data?.detail ||
          e.message ||
          "Failed to load login history logs.";
        setError(msg);
      } else {
        setError("An unexpected error occurred.");
      }
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, roleFilter, activityFilter, loginDate]);

  const roleColors = (role: string) => {
    const normalized = role.toLowerCase();
    if (normalized.includes("admin")) return { bg: "#DBEAFE", fg: "#1D4ED8" };
    if (normalized.includes("crew")) return { bg: "#DCFCE7", fg: "#15803D" };
    if (normalized.includes("dev")) return { bg: "#F3E8FF", fg: "#7E22CE" };
    return { bg: "#FEF3C7", fg: "#B45309" };
  };

  // Fetch Midnight Login Reports
  const fetchMidnightReports = useCallback(async () => {
    try {
      setReportLoading(true);
      setReportError(null);

      const [preRes, postRes] = await Promise.allSettled([
        apiService.get<LoginReportResponse>(
          "/api/authentication/users/pre-midnight-login-report/"
        ),
        apiService.get<LoginReportResponse>(
          "/api/authentication/users/post-midnight-login-report/"
        ),
      ]);

      if (preRes.status === "fulfilled") {
        setPreReport(preRes.value.data);
      } else {
        console.error("Failed to load pre-midnight report:", preRes.reason);
      }

      if (postRes.status === "fulfilled") {
        setPostReport(postRes.value.data);
      } else {
        console.error("Failed to load post-midnight report:", postRes.reason);
      }

      if (preRes.status === "rejected" && postRes.status === "rejected") {
        setReportError("Failed to load midnight login reports.");
      }
    } catch (e) {
      console.error("Unexpected error fetching reports:", e);
      setReportError("An error occurred while fetching midnight reports.");
    } finally {
      setReportLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLoginMonitor();
  }, [fetchLoginMonitor]);

  useEffect(() => {
    fetchMidnightReports();
  }, [fetchMidnightReports]);

  // Handle Force Logout Submission with Toast Notifications
  const handleConfirmForceLogout = async () => {
    try {
      setForceLogoutLoading(true);
      const response = await apiService.post<ForceLogoutResponse>(
        "/api/authentication/users/force-logout-all/"
      );

      const loggedOutCount = response.data.users_logged_out_count ?? 0;
      const successMsg = `${loggedOutCount} user${loggedOutCount === 1 ? "" : "s"} were successfully logged out. Your administrator session remains active.`;

      toast.success(successMsg, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      setForceLogoutDialogOpen(false);
      fetchLoginMonitor();
      fetchMidnightReports();
    } catch (e) {
      let errorMsg = "Failed to execute force logout.";
      if (e instanceof AxiosError) {
        if (e.response?.status === 403) {
          errorMsg = "Administrator privileges required to execute force logout.";
        } else if (e.response?.status === 401) {
          errorMsg = "Session expired. Please log in again.";
        } else if (e.response?.data?.detail) {
          errorMsg = e.response.data.detail;
        }
      }

      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setForceLogoutLoading(false);
    }
  };

  const getUserFullName = (user: CustomUser) => {
    return (
      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      user.username ||
      "System User"
    );
  };

  const formatLoginTime = (dateString: string | null) => {
    if (!dateString) return "Unknown Time";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;

    return (
      d.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) +
      " at " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const activeReportData = reportTab === 0 ? preReport : postReport;
  const activeReportUsers = activeReportData?.users || [];
  const activeReportCount = activeReportData?.count ?? activeReportUsers.length;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default, p: { xs: 2, md: 3 } }}>
      {/* 1. Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            fontSize: { xs: "1.5rem", md: "1.875rem" },
            mb: 0.5,
          }}
        >
          User Login & Session Monitor
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
          Monitor user login activity and manage system-wide sessions.
        </Typography>
      </Box>

      {/* 2. Session Administration (Superuser Only) */}
      {isSuperUser && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.secondary[800]}`,
            bgcolor: colors.secondary[500],
            p: 3,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.secondary[400], mb: 0.5 }}>
                  Session Administration
                </Typography>
                <Typography variant="body2" sx={{ color: colors.secondary[400], fontWeight: 500 }}>
                  Force all users out of the system except your current administrator session.
                </Typography>
                <Typography variant="caption" sx={{ color: colors.secondary[400], display: "block", mt: 0.5, opacity: 0.85 }}>
                  This immediately invalidates the login sessions of all other users and disconnects their active connections.
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<LogoutIcon />}
              onClick={() => setForceLogoutDialogOpen(true)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
                bgcolor: colors.secondary[400],
                color: "#FFFFFF",
                boxShadow: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  bgcolor: colors.primary[400],
                  boxShadow: `0 2px 8px ${colors.secondary[400]}40`,
                },
              }}
            >
              Force Logout All Users
            </Button>
          </Box>
        </Paper>
      )}

      {/* 3. Midnight Login Reports */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <NightlightIcon sx={{ color: colors.primary[400], fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Midnight Login Reports
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Audit logins during critical system maintenance windows
              </Typography>
            </Box>
          </Box>

          <Chip
            label={`${activeReportCount} User${activeReportCount === 1 ? "" : "s"}`}
            variant="filled"
            size="small"
            sx={{
              fontWeight: 600,
              bgcolor: colors.primary[400],
              color: "#FFF",
            }}
          />
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, bgcolor: theme.palette.mode === "dark" ? "transparent" : "#FAFAFA" }}>
          <Tabs
            value={reportTab}
            onChange={(_e, newValue) => setReportTab(newValue)}
            aria-label="midnight login report tabs"
            sx={{
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
                color: theme.palette.text.secondary,
              },
              "& .Mui-selected": {
                color: colors.primary[400],
              },
              "& .MuiTabs-indicator": {
                backgroundColor: colors.primary[400],
              },
            }}
          >
            <Tab
              icon={<NightlightIcon fontSize="small" />}
              iconPosition="start"
              label="Before Midnight (23:00 – 00:00)"
              id="report-tab-0"
              aria-controls="report-tabpanel-0"
            />
            <Tab
              icon={<SunIcon fontSize="small" />}
              iconPosition="start"
              label="After Midnight (00:00 – 00:10)"
              id="report-tab-1"
              aria-controls="report-tabpanel-1"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2, fontStyle: "italic" }}>
            {reportTab === 0
              ? 'Users who successfully logged in during the final hour before the midnight force logout (23:00:00 – 00:00:00 Africa/Harare).'
              : 'Users who successfully logged in during the first ten minutes after the midnight force logout (00:00:00 – 00:10:00 Africa/Harare).'}
          </Typography>

          {reportError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reportError}
            </Alert>
          )}

          <TableContainer sx={{ position: "relative", minHeight: 180 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.mode === "dark" ? "transparent" : "#FAFAFA" }}>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: 13 }}>
                    User
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: 13 }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: 13 }}>
                    Username
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportLoading ? (
                  Array.from({ length: 3 }).map((_, idx) => (
                    <TableRow key={`skeleton-${idx}`}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Skeleton variant="circular" width={36} height={36} />
                          <Skeleton variant="text" width={140} height={20} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={100} height={20} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : activeReportUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>
                      {reportTab === 0
                        ? "No users logged in between 23:00 and 00:00."
                        : "No users logged in between 00:00 and 00:10."}
                    </TableCell>
                  </TableRow>
                ) : (
                  activeReportUsers.map((user) => {
                    const avatarSrc = resolveAvatarSource(user.profile_picture);
                    const initials = getInitials(user.full_name);

                    return (
                      <Tooltip
                        key={`report-user-${user.user_id}`}
                        title={`Click to view details for ${user.full_name}`}
                        arrow
                      >
                        <TableRow
                          onClick={() => navigate(`/user-management/view/${user.user_id}`)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: theme.palette.action.hover },
                            "& td": { borderColor: theme.palette.divider },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Avatar
                                src={avatarSrc}
                                alt={user.full_name}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: colors.primary[400],
                                  fontSize: 14,
                                  fontWeight: 600,
                                }}
                              >
                                {initials}
                              </Avatar>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 14,
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {user.full_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.role || "User"}
                              size="small"
                              sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                bgcolor: roleColors(user.role || "").bg,
                                color: roleColors(user.role || "").fg,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 14, color: theme.palette.text.secondary }}>
                              {user.username}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </Tooltip>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* 4. Existing User Login History Monitor */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <HistoryIcon sx={{ color: colors.primary[400], fontSize: 26 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: theme.palette.text.primary }}
              >
                User Login History Monitor
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Total Records: {totalCount}
            </Typography>
          </Box>

          {/* Controls */}
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 135 }}>
              <InputLabel id="login-role-filter-label">Role</InputLabel>
              <Select labelId="login-role-filter-label" value={roleFilter} label="Role" onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">All roles</MenuItem>
                {[...new Set(logs.map((user) => user.role).filter(Boolean))].sort().map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="login-activity-filter-label">Last Login</InputLabel>
              <Select labelId="login-activity-filter-label" value={activityFilter} label="Last Login" onChange={(e) => { setActivityFilter(e.target.value); setLoginDate(""); setPage(1); }}>
                <MenuItem value="">Any time</MenuItem>
                <MenuItem value="today">Logged in Today</MenuItem>
                <MenuItem value="last_week">Last week</MenuItem>
                <MenuItem value="last_month">Last month</MenuItem>
              </Select>
            </FormControl>
            <TextField type="date" size="small" label="Login date" value={loginDate} onChange={(e) => { setLoginDate(e.target.value); setActivityFilter(""); setPage(1); }} InputLabelProps={{ shrink: true }} />
            <TextField
              placeholder="Search user or email..."
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              sx={{
                width: { xs: "100%", sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchLoginMonitor}
              disabled={loading}
              sx={{
                borderRadius: 2,
                bgcolor: colors.secondary[400],
                textTransform: "none",
                "&:hover": { bgcolor: colors.primary[400] },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {error && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Table Content */}
        <TableContainer sx={{ position: "relative", minHeight: "220px" }}>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255, 255, 255, 0.7)",
                zIndex: 2,
              }}
            >
              <CircularProgress color="inherit" size={30} />
            </Box>
          )}

          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === "dark" ? "transparent" : "#FAFAFA" }}>
                <TableCell
                  sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: 13 }}
                >
                  User
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: 13 }}
                >
                  Role
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: 13 }}
                >
                  Login Event Timestamp
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && !loading ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                    sx={{ py: 4, color: theme.palette.text.secondary }}
                  >
                    No login events tracked.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((user, idx) => {
                  const fullName = getUserFullName(user);
                  const avatarSrc = resolveAvatarSource(
                    user.profile_picture_url || user.profile_picture,
                    user.profile_picture_base64
                  );

                  const itemKey = `${user.id}-${user.last_login}-${idx}`;

                  return (
                    <Tooltip
                      key={itemKey}
                      title={`Click to view details for ${fullName}`}
                      arrow
                    >
                      <TableRow
                        onClick={() => navigate(`/user-management/view/${user.id}`)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: theme.palette.action.hover },
                          "& td": { borderColor: theme.palette.divider },
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 2 }}
                          >
                            <Avatar
                              src={avatarSrc}
                              alt={fullName}
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: colors.primary[400],
                                fontSize: 16,
                                fontWeight: 600,
                              }}
                            >
                              {getInitials(fullName)}
                            </Avatar>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 14,
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {fullName}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.role || "User"}
                            size="small"
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              bgcolor: roleColors(user.role || "").bg,
                              color: roleColors(user.role || "").fg,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: "#2E7D32",
                            }}
                          >
                            {formatLoginTime(user.last_login)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </Tooltip>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Navigation */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, marginRight: 1 }}>
            Page {page} of {Math.ceil(totalCount / rowsPerPage) || 1}
          </Typography>
          <Tooltip title="Previous Page">
            <span>
              <IconButton
                size="small"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  "&:disabled": { opacity: 0.5 },
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next Page">
            <span>
              <IconButton
                size="small"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(totalCount / rowsPerPage) || loading}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  "&:disabled": { opacity: 0.5 },
                }}
              >
                <KeyboardArrowRight />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {/* Force Logout Confirmation Dialog */}
      <Dialog
        open={forceLogoutDialogOpen}
        onClose={() => {
          if (!forceLogoutLoading) setForceLogoutDialogOpen(false);
        }}
        aria-labelledby="force-logout-dialog-title"
        aria-describedby="force-logout-dialog-description"
        PaperProps={{
          sx: { borderRadius: 3, p: 1, maxWidth: 480 },
        }}
      >
        <DialogTitle
          id="force-logout-dialog-title"
          sx={{ display: "flex", alignItems: "center", gap: 1.5, color: colors.primary[400] }}
        >
          <WarningIcon sx={{ fontSize: 28 }} />
          Force Logout All Users?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="force-logout-dialog-description" sx={{ color: theme.palette.text.primary }}>
            This will immediately sign out all other users currently using the system. Your administrator session will remain active.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setForceLogoutDialogOpen(false)}
            disabled={forceLogoutLoading}
            sx={{ textTransform: "none", color: theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmForceLogout}
            variant="contained"
            disabled={forceLogoutLoading}
            startIcon={
              forceLogoutLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <LogoutIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: colors.secondary[400],
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: colors.primary[400],
              },
            }}
          >
            {forceLogoutLoading ? "Executing..." : "Force Logout"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
